import React, { useState } from 'react';
import axios from 'axios';
import { requestID, baseUrl, config } from '../common/utils';
import {
  IonPage,
  IonContent,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton
} from '@ionic/react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Divider,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Send,
  Person,
  Description,
  CheckCircle
} from '@mui/icons-material';

const FeedbackForm = ({ token }) => {
  const [formData, setFormData] = useState({
    role: '',
    text: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setShowSuccess(false);

    try {
      const payload = { 
        requestID: requestID(),
        ...formData
      };
      
      const res = await axios.post(
        `${baseUrl()}performance/create`,
        payload,
        config
      );

      if (res.data.success) {
        setMessage('Feedback submitted successfully!');
        setShowSuccess(true);
        setFormData({ role: '', text: '' });
        
        // Auto-hide success message after 3 seconds
        setTimeout(() => setShowSuccess(false), 3000);
      }
    } catch (error) {
      setMessage('Failed to save feedback. Please try again.');
      setShowSuccess(false);
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <IonPage className="pushDown">
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/" />
          </IonButtons>
          <IonTitle>Submit Feedback</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <Box sx={{ p: 2, maxWidth: 800, margin: '0 auto' }}>
          {/* Success Alert */}
          {showSuccess && (
            <Alert 
              severity="success" 
              icon={<CheckCircle />}
              sx={{ mb: 3 }}
              onClose={() => setShowSuccess(false)}
            >
              {message}
            </Alert>
          )}

          {/* Error Alert */}
          {message && !showSuccess && (
            <Alert 
              severity="error" 
              sx={{ mb: 3 }}
              onClose={() => setMessage('')}
            >
              {message}
            </Alert>
          )}

          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h5" component="h1" gutterBottom sx={{ 
              fontWeight: 600, 
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <Send sx={{ fontSize: 28 }} />
              Provide Feedback
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Share your valuable feedback to help improve performance and collaboration.
            </Typography>

            <Divider sx={{ mb: 3 }} />

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Your Role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    placeholder="e.g., Manager, Team Lead, Colleague"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Feedback"
                    name="text"
                    value={formData.text}
                    onChange={handleChange}
                    required
                    multiline
                    rows={6}
                    variant="outlined"
                    placeholder="Share your constructive feedback here..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                          <Description color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        alignItems: 'flex-start'
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading}
                    fullWidth
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      textTransform: 'none'
                    }}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Send />}
                  >
                    {loading ? 'Submitting...' : 'Submit Feedback'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>

          {/* Guidelines Card */}
          <Card sx={{ mt: 3, borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                📝 Feedback Guidelines
              </Typography>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2" color="text.secondary" paragraph>
                  • Be specific and provide examples
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  • Focus on behaviors, not personalities
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  • Offer constructive suggestions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Maintain a professional and respectful tone
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default FeedbackForm;